import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { CollectorEventBucket, Segment } from '../availability.model';
import {
  DAY_MINUTES,
  MAX_SEGMENTS_PER_DAY,
  MAX_TOTAL_MINUTES,
  MIN_SEG_MINUTES,
  SNAP_MINUTES,
  clamp,
  floorToStep,
  formatTime,
  isSegmentListValid,
  nextSegmentId,
  snap,
  totalMinutes
} from '../day-timeline.utils';

type DragMode =
  | { type: 'create'; startMin: number }
  | {
      type: 'move';
      segId: string;
      offset: number;
      origStart: number;
      origEnd: number;
    }
  | { type: 'resize-start'; segId: string; origEnd: number }
  | { type: 'resize-end'; segId: string; origStart: number };

@Component({
  selector: 'app-day-timeline',
  templateUrl: './day-timeline.component.html',
  styleUrls: ['./day-timeline.component.css']
})
export class DayTimelineComponent implements OnDestroy {
  @Input() date!: string;
  @Input() segments: Segment[] = [];
  @Input() events: CollectorEventBucket[] = [];
  @Input() disabled = false;
  @Input() maxSegments = MAX_SEGMENTS_PER_DAY;
  @Output() segmentsChange = new EventEmitter<Segment[]>();

  @ViewChild('track', { static: true })
  trackRef!: ElementRef<HTMLDivElement>;

  draftSeg: Segment | null = null;

  readonly hourGridIndices = Array.from({ length: 25 }, (_, h) => h);
  readonly hourLabels = [0, 6, 12, 18, 24];
  readonly formatTime = formatTime;

  private drag: DragMode | null = null;
  private readonly onPointerMoveBound = (e: PointerEvent) =>
    this.onPointerMove(e);
  private readonly onPointerUpBound = () => this.onPointerUp();

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.detachWindowListeners();
  }

  get dayEvents(): CollectorEventBucket[] {
    return this.events.filter((e) => e.date === this.date);
  }

  get maxEventCount(): number {
    return this.dayEvents.reduce((m, e) => Math.max(m, e.count), 1);
  }

  get creationLimitMessage(): string {
    const remainingMinutes = MAX_TOTAL_MINUTES - totalMinutes(this.segments);
    if (this.disabled || remainingMinutes >= MIN_SEG_MINUTES) {
      return '';
    }
    if (remainingMinutes <= 0) {
      return `Daily limit reached (${MAX_TOTAL_MINUTES / 60} hours).`;
    }
    return `Only ${remainingMinutes} minutes remaining. New intervals require at least ${MIN_SEG_MINUTES} minutes.`;
  }

  eventLeftPercent(event: CollectorEventBucket): number {
    const bucketStartMinutes = event.hour * 60 + event.minute;
    return ((bucketStartMinutes + SNAP_MINUTES / 2) / DAY_MINUTES) * 100;
  }

  eventDotSize(event: CollectorEventBucket): number {
    return 6 + (event.count / this.maxEventCount) * 10;
  }

  eventTooltip(event: CollectorEventBucket): string {
    const parts = Object.entries(event.breakdown)
      .map(([key, value]) => `${key} ${value}`)
      .join(' · ');
    return `${formatTime(event.hour * 60 + event.minute)} — ${
      event.count
    } games${parts ? ` (${parts})` : ''}`;
  }

  segmentLeftPercent(seg: Segment): number {
    return (seg.startTime / DAY_MINUTES) * 100;
  }

  segmentWidthPercent(seg: Segment): number {
    return ((seg.endTime - seg.startTime) / DAY_MINUTES) * 100;
  }

  segmentLabel(seg: Segment): string {
    return `${formatTime(seg.startTime)} – ${formatTime(seg.endTime)}`;
  }

  hasClippedSegmentLabel(seg: Segment): boolean {
    return seg.endTime - seg.startTime < 120;
  }

  hourLineLeftPercent(hour: number): number {
    return (hour / 24) * 100;
  }

  isMajorGridline(hour: number): boolean {
    return hour % 6 === 0;
  }

  trackByEvent(_: number, event: CollectorEventBucket): string {
    return `${event.date}-${event.hour}-${event.minute}`;
  }

  trackBySegmentId(_: number, segment: Segment): string {
    return segment.id;
  }

  onTrackPointerDown(event: PointerEvent): void {
    if (this.disabled) {
      return;
    }
    if (this.segments.length >= this.maxSegments) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('[data-segment]')) {
      return;
    }
    const min = this.minutesFromClientX(event.clientX);
    const draft = this.createDraftSegment(min, min);
    if (!draft) {
      return;
    }
    this.beginDrag({ type: 'create', startMin: min });
    this.draftSeg = draft;
  }

  onSegmentPointerDown(event: PointerEvent, seg: Segment): void {
    if (this.disabled) {
      return;
    }
    event.stopPropagation();
    const min = this.minutesFromClientX(event.clientX);
    this.beginDrag({
      type: 'move',
      segId: seg.id,
      offset: min - seg.startTime,
      origStart: seg.startTime,
      origEnd: seg.endTime
    });
  }

  onResizeStartPointerDown(event: PointerEvent, seg: Segment): void {
    if (this.disabled) {
      return;
    }
    event.stopPropagation();
    this.beginDrag({
      type: 'resize-start',
      segId: seg.id,
      origEnd: seg.endTime
    });
  }

  onResizeEndPointerDown(event: PointerEvent, seg: Segment): void {
    if (this.disabled) {
      return;
    }
    event.stopPropagation();
    this.beginDrag({
      type: 'resize-end',
      segId: seg.id,
      origStart: seg.startTime
    });
  }

  removeSegment(event: Event, id: string): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.emitSegments(this.segments.filter((s) => s.id !== id));
  }

  private beginDrag(mode: DragMode): void {
    this.drag = mode;
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', this.onPointerMoveBound);
      window.addEventListener('pointerup', this.onPointerUpBound);
    });
  }

  private detachWindowListeners(): void {
    window.removeEventListener('pointermove', this.onPointerMoveBound);
    window.removeEventListener('pointerup', this.onPointerUpBound);
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.drag) {
      return;
    }
    const min = this.minutesFromClientX(event.clientX);

    if (this.drag.type === 'create') {
      const draft = this.createDraftSegment(this.drag.startMin, min);
      this.zone.run(() => {
        this.draftSeg = draft;
        this.cdr.markForCheck();
      });
      return;
    }

    if (this.drag.type === 'move') {
      const drag = this.drag;
      const len = drag.origEnd - drag.origStart;
      let newStart = clamp(snap(min - drag.offset), 0, DAY_MINUTES - len);
      let newEnd = newStart + len;
      const others = this.segments.filter((s) => s.id !== drag.segId);
      for (const o of others) {
        if (newStart < o.endTime && newEnd > o.startTime) {
          if (drag.origStart < o.startTime) {
            newEnd = o.startTime;
            newStart = newEnd - len;
          } else {
            newStart = o.endTime;
            newEnd = newStart + len;
          }
        }
      }
      newStart = clamp(newStart, 0, DAY_MINUTES - len);
      newEnd = newStart + len;
      const next = this.segments.map((s) =>
        s.id === drag.segId ? { ...s, startTime: newStart, endTime: newEnd } : s
      );
      this.zone.run(() => this.emitSegments(next));
      return;
    }

    if (this.drag.type === 'resize-start') {
      const drag = this.drag;
      let newStart = clamp(snap(min), 0, drag.origEnd - MIN_SEG_MINUTES);
      const prev = this.segments
        .filter((s) => s.id !== drag.segId && s.endTime <= drag.origEnd)
        .reduce<Segment | null>(
          (acc, s) =>
            s.endTime > (acc?.endTime ?? -1) && s.endTime <= drag.origEnd
              ? s
              : acc,
          null
        );
      if (prev) {
        newStart = Math.max(newStart, prev.endTime);
      }
      const next = this.segments.map((s) =>
        s.id === drag.segId ? { ...s, startTime: newStart } : s
      );
      this.zone.run(() => this.emitSegmentsIfValid(next));
      return;
    }

    if (this.drag.type === 'resize-end') {
      const drag = this.drag;
      let newEnd = clamp(
        snap(min),
        drag.origStart + MIN_SEG_MINUTES,
        DAY_MINUTES
      );
      const nextSeg = this.segments
        .filter((s) => s.id !== drag.segId && s.startTime >= drag.origStart)
        .reduce<Segment | null>(
          (acc, s) =>
            s.startTime < (acc?.startTime ?? Infinity) &&
            s.startTime >= drag.origStart
              ? s
              : acc,
          null
        );
      if (nextSeg) {
        newEnd = Math.min(newEnd, nextSeg.startTime);
      }
      const next = this.segments.map((s) =>
        s.id === drag.segId ? { ...s, endTime: newEnd } : s
      );
      this.zone.run(() => this.emitSegmentsIfValid(next));
    }
  }

  private createDraftSegment(anchor: number, minute: number): Segment | null {
    const remainingMinutes = floorToStep(
      MAX_TOTAL_MINUTES - totalMinutes(this.segments)
    );
    if (remainingMinutes < MIN_SEG_MINUTES) {
      return null;
    }
    const duration = clamp(
      Math.abs(minute - anchor),
      MIN_SEG_MINUTES,
      remainingMinutes
    );
    const start = clamp(
      minute < anchor ? anchor - duration : anchor,
      0,
      DAY_MINUTES - duration
    );
    return { id: 'draft', startTime: start, endTime: start + duration };
  }

  private onPointerUp(): void {
    const drag = this.drag;
    const draft = this.draftSeg;
    this.detachWindowListeners();

    this.zone.run(() => {
      if (
        drag?.type === 'create' &&
        draft &&
        this.segments.length < this.maxSegments
      ) {
        const proposed = [...this.segments, { ...draft, id: nextSegmentId() }];
        if (isSegmentListValid(proposed)) {
          this.emitSegments(
            [...proposed].sort((a, b) => a.startTime - b.startTime)
          );
        }
      }
      this.drag = null;
      this.draftSeg = null;
      this.cdr.markForCheck();
    });
  }

  private emitSegments(next: Segment[]): void {
    this.segments = next;
    this.segmentsChange.emit(next);
  }

  private emitSegmentsIfValid(next: Segment[]): void {
    if (isSegmentListValid(next)) {
      this.emitSegments(next);
    }
  }

  private minutesFromClientX(clientX: number): number {
    const el = this.trackRef?.nativeElement;
    if (!el) {
      return 0;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) {
      return 0;
    }
    const ratio = (clientX - rect.left) / rect.width;
    return clamp(snap(ratio * DAY_MINUTES), 0, DAY_MINUTES);
  }
}
